import React, { useState } from "react";
import { RNMaterialnoActualRow, RNRazmerActualRow } from "./useRNWorkOrder";
import { RNInterProductActualRow } from "../types/Types";
import { RazmerRow } from "../components/OSRazmerTable";
import { MatGroup, MatItem } from "../components/OSMaterialnoTable";
import { SubprocessItemRow } from "../components/OSSubprocessItemsTable";
import { parseDecimalInput, formatForEdit } from "../utils/decimalInput";

// A saved actual quantity/waste/rastur/uzurkuvanje arrives in canonical dot form
// ("2.5") from the server (which now accepts both comma and dot on save). The MK
// keyboard's decimal key is a comma, so the editable field must show it the MK way
// ("2,5") — otherwise a value the user never touched appears to change the moment
// it's loaded. Only re-format values that actually parse as a number; anything else
// (including empty string) passes through unchanged so it never masks a real problem.
const formatSavedQtyForEdit = (value: string | undefined): string | undefined => {
  if (value === undefined || value === null || value === '') return value;
  const parsed = parseDecimalInput(value);
  return parsed !== null ? formatForEdit(parsed) : value;
};

// Owns the three RN "actuals" arrays (Размерување / Материјално / Материјално прием)
// that get persisted to /api/rn-work-orders, plus the merge/build helpers that map them
// onto and off of the OS-shaped row state (razmerRows / materialnoGroups /
// subprocessItemRows). Pulled out of RNWorkOrder.tsx as a pure move — same state, same
// refs, same functions, only the declaration site changed.
export const useRNWorkOrderActuals = () => {
  const [, setRazmerActualRows] = useState<RNRazmerActualRow[]>([]);
  const razmerActualRowsRef = React.useRef<RNRazmerActualRow[]>([]);
  const [, setMaterialnoActualRows] = useState<RNMaterialnoActualRow[]>([]);
  const materialnoActualRowsRef = React.useRef<RNMaterialnoActualRow[]>([]);
  // Материјално прием (7201) actuals — RN-owned measured RECEIVED quantities, merged
  // directly onto subprocessItemRows (below) same as materialnoActuals is merged onto
  // materialnoGroups.items — see mergeInterProductActuals.
  const [, setInterProductActualRows] = useState<RNInterProductActualRow[]>([]);
  const interProductActualRowsRef = React.useRef<RNInterProductActualRow[]>([]);

  // Key is the OS row's own _id only. Never fall back to a composite of item/qty
  // fields — razmerActuals[].rowKey is stored in plaintext outside the encrypted
  // recipe field, so it must never carry the item ident or required quantity.
  // A row without osRowId gets '' and is dropped by the rowKey filter below.
  const buildRazmerRowKey = React.useCallback((row: Partial<RazmerRow>): string => (
    row.osRowId || ''
  ), []);

  const mergeRazmerActuals = React.useCallback((rows: RazmerRow[], actuals: RNRazmerActualRow[]) => {
    const actualsBySource = new Map<string, RNRazmerActualRow[]>();
    actuals.forEach((actual) => {
      const source = actual.sourceRowKey || actual.rowKey;
      actualsBySource.set(source, [...(actualsBySource.get(source) || []), actual]);
    });
    const mergeRow = (row: RazmerRow, actual?: RNRazmerActualRow): RazmerRow => ({
      ...row,
      osRowId: actual?.rowKey ?? row.osRowId,
      sourceRowKey: actual?.sourceRowKey ?? row.sourceRowKey ?? row.osRowId ?? '',
      measuredQty: formatSavedQtyForEdit(actual?.measuredQty) ?? row.measuredQty ?? '',
      serialNumber: actual?.serialNumber ?? row.serialNumber ?? '', expiryDate: actual?.expiryDate ?? row.expiryDate ?? '',
      waste: formatSavedQtyForEdit(actual?.waste) ?? row.waste ?? '', rastur: formatSavedQtyForEdit(actual?.rastur) ?? row.rastur ?? '',
      isPreviewActual: actual ? false : row.isPreviewActual, workedBy: actual?.workedBy ?? row.workedBy,
      workedByName: actual?.workedByName ?? row.workedByName ?? '', workedAt: actual?.workedAt ?? row.workedAt ?? '',
      verifiedBy: actual?.verifiedBy ?? row.verifiedBy, verifiedByName: actual?.verifiedByName ?? row.verifiedByName ?? '',
      verifiedAt: actual?.verifiedAt ?? row.verifiedAt ?? '', verifyRequested: row.verifyRequested ?? false,
      confirmed: actual?.confirmed ?? row.confirmed ?? false,
    });
    const mergedSources = new Set<string>();
    return rows.flatMap((row, index) => {
      const sourceRowKey = row.sourceRowKey || buildRazmerRowKey(row);
      const dedupeKey = sourceRowKey || `unsaved-${index}`;
      if (mergedSources.has(dedupeKey)) return [];
      mergedSources.add(dedupeKey);
      const sourceActuals = actualsBySource.get(sourceRowKey) || [];
      return sourceActuals.length ? sourceActuals.map((actual) => mergeRow(row, actual)) : [mergeRow(row)];
    });
  }, [buildRazmerRowKey]);

  const setRazmerActuals = React.useCallback((rows: RNRazmerActualRow[]) => {
    razmerActualRowsRef.current = rows;
    setRazmerActualRows(rows);
  }, []);

  const buildMaterialnoRowKey = React.useCallback((row: Partial<MatItem>): string => (
    row.osRowId || ''
  ), []);

  const mergeMaterialnoActuals = React.useCallback((groups: MatGroup[], actuals: RNMaterialnoActualRow[]) => {
    const actualByKey = new Map(actuals.map((r) => [r.rowKey, r]));
    return groups.map((group) => ({
      ...group,
      items: group.items.map((item) => {
        const actual = actualByKey.get(buildMaterialnoRowKey(item));
        return {
          ...item,
          measuredQty: formatSavedQtyForEdit(actual?.measuredQty) ?? item.measuredQty ?? '',
          serialNumber: actual?.serialNumber ?? item.serialNumber ?? '',
          expiryDate: actual?.expiryDate ?? item.expiryDate ?? '',
          waste: formatSavedQtyForEdit(actual?.waste) ?? item.waste ?? '',
          rastur: formatSavedQtyForEdit(actual?.rastur) ?? item.rastur ?? '',
          workedBy: actual?.workedBy ?? item.workedBy,
          workedByName: actual?.workedByName ?? item.workedByName ?? '',
          workedAt: actual?.workedAt ?? item.workedAt ?? '',
          verifiedBy: actual ? actual.verifiedBy : item.verifiedBy,
          verifiedByName: actual ? actual.verifiedByName || '' : item.verifiedByName ?? '',
          verifiedAt: actual ? actual.verifiedAt || '' : item.verifiedAt ?? '',
          verifyRequested: item.verifyRequested ?? false,
          confirmed: actual?.confirmed ?? false,
        };
      }),
    }));
  }, [buildMaterialnoRowKey]);

  const setMaterialnoActuals = React.useCallback((rows: RNMaterialnoActualRow[]) => {
    materialnoActualRowsRef.current = rows;
    setMaterialnoActualRows(rows);
  }, []);

  const toRazmerActualRows = React.useCallback((rows: RazmerRow[]): RNRazmerActualRow[] => (
    rows
      .filter((row) => !row.isPreviewActual)
      .map((row) => ({
        rowKey: buildRazmerRowKey(row),
        sourceRowKey: row.sourceRowKey || row.osRowId || '',
        measuredQty: row.measuredQty || '',
        serialNumber: row.serialNumber || '',
        expiryDate: row.expiryDate || '',
        waste: row.waste || '',
        rastur: row.rastur || '',
        verifyRequested: !!row.verifyRequested,
        confirmed: !!row.confirmed,
      }))
      .filter((row) => !!(row.rowKey && (row.measuredQty || row.serialNumber || row.expiryDate || row.waste || row.rastur)))
  ), [buildRazmerRowKey]);

  const toMaterialnoActualRows = React.useCallback((groups: MatGroup[]): RNMaterialnoActualRow[] => (
    groups
      .flatMap((group) => group.items.map((item) => ({
        rowKey: buildMaterialnoRowKey(item),
        measuredQty: item.measuredQty || '',
        serialNumber: item.serialNumber || '',
        expiryDate: item.expiryDate || '',
        waste: item.waste || '',
        rastur: item.rastur || '',
        verifyRequested: !!item.verifyRequested,
        // Операторовата ✓ (OSMaterialnoTable) — НЕ се изведува од содржината на редот.
        // Ако тука се препише, откачувањето на „конфирмирано" при рачна измена
        // (rule 4 во OSMaterialnoTable) тивко се поништува на следното снимање.
        confirmed: !!item.confirmed,
      })))
      .filter((row) => !!(row.rowKey && (row.measuredQty || row.serialNumber || row.expiryDate || row.waste || row.rastur)))
  ), [buildMaterialnoRowKey]);

  // Материјално прием (7201) actuals — rowKey is the interProducts[] subdocument's own
  // `_id` (SubprocessItemRow.rowId), same convention as razmer/materialno rowKeys being
  // the OS row's own _id. `confirmed` is an EXPLICIT operator checkbox here too
  // (interProductActuals.confirmed is a real stored field, not derived), so
  // toInterProductActualRows keeps the user's checkbox value verbatim — same as
  // toRazmerActualRows and toMaterialnoActualRows.
  const buildInterProductRowKey = React.useCallback((row: Partial<SubprocessItemRow>): string => (
    row.rowId || ''
  ), []);

  const mergeInterProductActuals = React.useCallback((rows: SubprocessItemRow[], actuals: RNInterProductActualRow[]): SubprocessItemRow[] => {
    const actualByKey = new Map(actuals.map((r) => [r.rowKey, r]));
    return rows.map((row) => {
      const actual = actualByKey.get(buildInterProductRowKey(row));
      return {
        ...row,
        measuredQty: formatSavedQtyForEdit(actual?.measuredQty) ?? row.measuredQty ?? '',
        serialNumber: actual?.serialNumber ?? row.serialNumber ?? '',
        expiryDate: actual?.expiryDate ?? row.expiryDate ?? '',
        waste: formatSavedQtyForEdit(actual?.waste) ?? row.waste ?? '',
        rastur: formatSavedQtyForEdit(actual?.rastur) ?? row.rastur ?? '',
        uzurkuvanje: formatSavedQtyForEdit(actual?.uzurkuvanje) ?? row.uzurkuvanje ?? '',
        deviationReason: actual?.deviationReason ?? row.deviationReason ?? '',
        workedBy: actual?.workedBy ?? row.workedBy,
        workedByName: actual?.workedByName ?? row.workedByName ?? '',
        workedAt: actual?.workedAt ?? row.workedAt ?? '',
        verifiedBy: actual ? actual.verifiedBy : row.verifiedBy,
        verifiedByName: actual ? actual.verifiedByName || '' : row.verifiedByName ?? '',
        verifiedAt: actual ? actual.verifiedAt || '' : row.verifiedAt ?? '',
        verifyRequested: row.verifyRequested ?? false,
        confirmed: actual?.confirmed ?? false,
      };
    });
  }, [buildInterProductRowKey]);

  const setInterProductActuals = React.useCallback((rows: RNInterProductActualRow[]) => {
    interProductActualRowsRef.current = rows;
    setInterProductActualRows(rows);
  }, []);

  const toInterProductActualRows = React.useCallback((rows: SubprocessItemRow[]): RNInterProductActualRow[] => (
    rows
      .map((row) => ({
        rowKey: buildInterProductRowKey(row),
        measuredQty: row.measuredQty || '',
        serialNumber: row.serialNumber || '',
        expiryDate: row.expiryDate || '',
        waste: row.waste || '',
        rastur: row.rastur || '',
        uzurkuvanje: row.uzurkuvanje || '',
        deviationReason: row.deviationReason || '',
        verifyRequested: !!row.verifyRequested,
        confirmed: !!row.confirmed,
      }))
      .filter((row) => !!(row.rowKey && (row.measuredQty || row.serialNumber || row.expiryDate || row.waste || row.rastur || row.uzurkuvanje)))
  ), [buildInterProductRowKey]);

  return {
    razmerActualRowsRef,
    materialnoActualRowsRef,
    interProductActualRowsRef,
    mergeRazmerActuals,
    setRazmerActuals,
    mergeMaterialnoActuals,
    setMaterialnoActuals,
    toRazmerActualRows,
    toMaterialnoActualRows,
    mergeInterProductActuals,
    setInterProductActuals,
    toInterProductActualRows,
  };
};
